import React from 'react';
import { useRouter } from 'next/navigation';
import { Plan } from '../../../lib/supabase';

interface PlanCardProps {
  plan: Plan;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
}

export default function PlanCard({ plan, isSelected, onSelect, onDelete }: PlanCardProps) {
  const router = useRouter();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleViewDetails = () => {
    router.push(`/ai-plan?planId=${plan.id}`);
  };

  return (
    <div className={`bg-white rounded-lg shadow-md p-6 transition-all duration-200 hover:shadow-lg ${
      isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : ''
    }`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onSelect}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3"
          />
          <h3 className="text-lg font-semibold text-gray-900 truncate">
            {plan?.plan_name || plan?.objective || 'Untitled Plan'}
          </h3>
        </div>
        <button
          onClick={onDelete}
          className="text-red-600 hover:text-red-800 text-sm font-medium"
        >
          Delete
        </button>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex items-center text-sm text-gray-600">
          <span className="font-medium mr-2">Space:</span>
          <span>{plan?.location || 'Not specified'}</span>
        </div>
        <div className="flex items-center text-sm text-gray-600">
          <span className="font-medium mr-2">Dimensions:</span>
          <span>{plan?.dimensions || 'Not specified'}</span>
        </div>
        <div className="flex items-center text-sm text-gray-600">
          <span className="font-medium mr-2">Budget:</span>
          <span>{plan?.budget || 'Not specified'}</span>
        </div>
        <div className="flex items-center text-sm text-gray-600">
          <span className="font-medium mr-2">Sunlight:</span>
          <span>{plan?.sunlight || 'Not specified'}</span>
        </div>
      </div>

      {plan?.details && (
        <div className="mb-4">
          <p className="text-sm text-gray-700 line-clamp-3">
            {plan.details}
          </p>
        </div>
      )}

      {plan?.ai_response && (
        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
          <h4 className="text-sm font-medium text-blue-900 mb-1">AI Recommendation:</h4>
          <p className="text-sm text-blue-800 line-clamp-2">
            {plan.ai_response}
          </p>
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>Created: {formatDate(plan?.created_at || '')}</span>
        <button 
          onClick={handleViewDetails}
          className="text-blue-600 hover:text-blue-800 font-medium"
        >
          View Details
        </button>
      </div>
    </div>
  );
}